import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Cookie, ShieldCheck } from "lucide-react";

const STORAGE_KEY = "anc_cookie_consent";

const Toggle = ({ checked, disabled, onChange, testId }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    data-testid={testId}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 ${
      checked ? "bg-brand" : "bg-gray-300"
    } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-300 ${
        checked ? "translate-x-6" : "translate-x-1"
      }`}
    />
  </button>
);

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) setVisible(true);
  }, []);

  const persist = (consent) => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
    setVisible(false);
  };

  const acceptAll = () =>
    persist({ necessary: true, analytics: true, marketing: true });

  const rejectAll = () =>
    persist({ necessary: true, analytics: false, marketing: false });

  const savePrefs = () =>
    persist({ necessary: true, analytics, marketing });

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-x-0 bottom-0 z-[100] px-4 pb-4 sm:pb-6 flex justify-center"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 40 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        data-testid="cookie-consent"
        role="dialog"
        aria-modal="false"
        aria-label="Preferințe privind cookie-urile"
      >
        <div className="w-full max-w-2xl rounded-2xl bg-white shadow-lift ring-1 ring-black/5 p-6 sm:p-7">
          <div className="flex items-start gap-4">
            <span className="hidden sm:flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-light text-brand">
              <Cookie className="h-6 w-6" />
            </span>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-ink">Preferințe privind cookie-urile</h3>
              <p className="mt-2 text-sm text-ink-muted leading-relaxed">
                Folosim cookie-uri necesare pentru funcționarea corectă a website-ului și,
                cu acordul tău, cookie-uri pentru analiză și marketing. Poți accepta toate
                cookie-urile, îți poți personaliza preferințele sau le poți refuza pe cele
                opționale. Vezi{" "}
                <Link
                  to="/politica-de-cookies"
                  data-testid="cookie-policy-link"
                  className="text-brand font-medium hover:underline"
                >
                  Politica de cookies
                </Link>
                .
              </p>
            </div>
          </div>

          <AnimatePresence initial={false}>
            {showPrefs && (
              <motion.div
                key="prefs"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
                data-testid="cookie-prefs-panel"
              >
                <div className="mt-5 space-y-3 border-t border-gray-100 pt-5">
                  <div className="flex items-start justify-between gap-4 rounded-xl bg-gray-50 px-4 py-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4 text-brand" />
                        <p className="text-sm font-semibold text-ink">Cookie-uri necesare</p>
                        <span className="text-xs font-medium text-brand">Mereu active</span>
                      </div>
                      <p className="mt-1 text-xs text-ink-muted leading-relaxed">
                        Aceste cookie-uri sunt necesare pentru funcționarea corectă și
                        securitatea website-ului.
                      </p>
                    </div>
                    <Toggle checked disabled onChange={() => {}} testId="cookie-toggle-necessary" />
                  </div>

                  <div className="flex items-start justify-between gap-4 rounded-xl bg-gray-50 px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold text-ink">Analiză și statistici</p>
                      <p className="mt-1 text-xs text-ink-muted leading-relaxed">
                        Ne ajută să înțelegem cum este utilizat website-ul și să îi
                        îmbunătățim performanța.
                      </p>
                    </div>
                    <Toggle checked={analytics} onChange={setAnalytics} testId="cookie-toggle-analytics" />
                  </div>

                  <div className="flex items-start justify-between gap-4 rounded-xl bg-gray-50 px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold text-ink">Marketing și publicitate</p>
                      <p className="mt-1 text-xs text-ink-muted leading-relaxed">
                        Permit afișarea și măsurarea unor campanii de marketing relevante.
                      </p>
                    </div>
                    <Toggle checked={marketing} onChange={setMarketing} testId="cookie-toggle-marketing" />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            {showPrefs ? (
              <button
                onClick={savePrefs}
                data-testid="cookie-save-prefs"
                className="w-full inline-flex items-center justify-center rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white hover:bg-brand-hover transition-colors duration-300"
              >
                Salvează preferințele
              </button>
            ) : (
              <>
                <button
                  onClick={acceptAll}
                  data-testid="cookie-accept-all"
                  className="w-full sm:flex-1 inline-flex items-center justify-center rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white hover:bg-brand-hover transition-colors duration-300"
                >
                  Acceptă toate
                </button>
                <button
                  onClick={() => setShowPrefs(true)}
                  data-testid="cookie-accept-partial"
                  className="w-full sm:flex-1 inline-flex items-center justify-center rounded-full border border-gray-300 px-6 py-3 text-sm font-semibold text-ink hover:border-brand hover:text-brand transition-colors duration-300"
                >
                  Acceptă parțial
                </button>
                <button
                  onClick={rejectAll}
                  data-testid="cookie-reject"
                  className="w-full sm:flex-1 inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold text-ink-muted hover:text-ink transition-colors duration-300"
                >
                  Refuză
                </button>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
